import { Big } from 'big.js';
import BreJob from '~/tenants/bre/model/BreJob';
import BreSocialConfig from '~/tenants/bre/performable/social/BreSocialConfig';
import { TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { BreSocialRate } from '~/tenants/bre/performable/social/BreSocialData';
import { bre_split_video } from '~/tenants/bre/performable/common';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';

export default class BreSocialJob extends BreJob<typeof BreSocialConfig> {
  get performable(): typeof BreSocialConfig {
    return BreSocialConfig;
  }

  get configurable(): boolean {
    // upsell boosting, aerial, intro and narration
    return true;
  }

  get submission(): boolean {
    // photographer has to declare if they are self-editing video
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.RENTAL);
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreSocialConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
    };
  }

  onsite(): number {
    let onsite = 45;

    if (this.metadata.intro) {
      onsite += 15;
    }

    return onsite;
  }

  revenueLines(): TenantJobLine[] {
    const lines: TenantJobLine[] = [];

    lines.push({
      id: 'video',
      description: 'Vertical Ground Video Footage',
      amount: new Big(BreSocialRate.BASE_VIDEO),
      discountable: true,
    });

    if (this.metadata.intro) {
      lines.push({
        id: 'intro',
        description: 'Agent Intro',
        amount: new Big(BreSocialRate.INTRO),
      });
    }

    if (this.metadata.narration) {
      lines.push({
        id: 'narration',
        description: 'Agent Narration',
        amount: new Big(BreSocialRate.NARRATION),
      });
    }

    if (this.metadata.voiceover) {
      lines.push({
        id: 'voiceover',
        description: `Professional Voice-Over (${this.metadata.voiceover})`,
        amount: new Big(BreSocialRate.VOICEOVER),
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const lines: TenantJobLine[] = this.revenueLines()
      .filter((l) => l.id !== 'voiceover')
      .map((r) => ({
        ...r,
        amount: bre_split_video(r.amount, !this.metadata.self_edited),
      }));

    if (this.metadata.voiceover) {
      lines.push({
        id: 'voiceover',
        description: `Professional Voice-Over (${this.metadata.voiceover})`,
        provider_id: (this.metadata.voiceover === 'male'
          ? BreProviderId.KWASI_ACHIAW
          : BreProviderId.ALICIA_GARCIA
        ).toString(),
        phase: 'completed',
        amount: new Big('75'),
      });
    }

    return lines;
  }
}
